/* eslint-disable jsx-a11y/media-has-caption */
import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { BasicLoader } from '@acko-tech/building-blocks.ui.loaders';
import Searchbar from './SearchPage';
import Card from '../common/Card/Index';
import {
  SectionTitle,
  SectionSubtitle,
  PopularTitle,
  Filters,
  FilterPill,
  BackIcon,
  Gradient,
  Section,
  CardsContainer,
  TitleGroup,
  NoResult,
  HeaderBanner,
  NoResultsText,
  PageContainer,
} from './styles';
import { FlexContainer } from 'src/common/styles';
import {
  visa2FlyFilters,
  v2fTaglines,
  UTM_MEDIUM_HOMEPAGE,
  UTM_MEDIUM_EXPLORE_TAB,
} from 'src/vas/config';
import { postRequest } from 'src/common/utils/api/request';
import { getDomainOrigin } from 'src/common/utils/serverConfig';
import { travelV2fUrls, VasFeBaseUrl } from 'src/common/utils/api/urls';
import { ICard, IPagination } from 'src/vas/interfaces';
import { ASSETS, ErrorMessages } from 'src/common/utils/config';
import { useRouterPush } from 'src/common/custom-hooks';
import { sendVasEvents } from 'src/common/utils/segment';
import { getQueryParams } from 'src/common/utils/generics';
import {
  EventType,
  TravelInsuranceTrackEvents,
  VasTrackEvents,
} from 'src/common/utils/segment/types';
import Toast from '@acko-tech/building-blocks.ui.toast/dist/toast';
import InfiniteScroll from 'react-infinite-scroll-component';

const Index = () => {
  const router = useRouter();
  const pushRoute = useRouterPush(VasFeBaseUrl);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState<IPagination>({
    page: 0,
    offset: 10,
  });
  const [selectedFilter, setSelectedFilter] = useState<string>('Trending');
  const [allResults, setAllResults] = useState<any>([]);
  const [searchResult, setSearchResult] = useState<any>(null);
  const [showToast, setShowToast] = useState<any>(null);
  const query = router?.query || {};

  useEffect(() => {
    if (!!query?.visaCountry) {
      const vC = query?.visaCountry as string;
      getSearchFilterResults({ country: vC?.toUpperCase() }, true);
    } else {
      const p = { page: 0, offset: 10 };
      setPagination(p);

      if (!!selectedFilter) {
        getSearchFilterResults({
          ...p,
          ...visa2FlyFilters[selectedFilter],
        });
      }
    }
  }, [selectedFilter, query?.visaCountry]);

  const handleCardClick = (country: string) => {
    setIsLoading(true);
    const userId = Cookies.get('user_id');
    const pathname = `${VasFeBaseUrl}/redirection/${country}${getQueryParams(
      query
    )}`;

    sendVasEvents(VasTrackEvents.TAP_BTN_COUNTRY_SELECTION, EventType.TAP, {
      destination_countries: !!country ? country : undefined,
    });

    if (userId) router.push(pathname);
    else {
      router.push({
        pathname: `/login`,
        query: { next: pathname },
      });
    }
  };

  const getSearchFilterResults = async (props, isSearch = false) => {
    setIsLoading(true);
    try {
      const res = await postRequest(
        `${getDomainOrigin()}${travelV2fUrls.v2fCountryResults()}`,
        { ...props }
      );
      setIsLoading(false);

      if (res?.data) {
        if (isSearch) {
          const searchedCountry =
            res?.data?.data?.countries?.length > 0
              ? res?.data?.data?.countries[0]
              : null;
          setSearchResult(searchedCountry);
        } else {
          setSearchResult(null);
          setAllResults(res?.data?.data?.countries);
        }
      }
    } catch (error: any) {
      setIsLoading(false);
      console.log({ error });

      const toastMsg =
        error?.response?.status === 500
          ? ErrorMessages.GENERIC
          : error?.response?.data?.message;

      setShowToast(toastMsg);
      setTimeout(() => {
        setShowToast(null);
        pushRoute(`${getQueryParams(router?.query)}`);
      }, 1000);
    }
  };

  const handleScroll = async () => {
    const p = { page: pagination?.page + 1, offset: 10 };
    setPagination(p);
    if (allResults?.length > 9) {
      try {
        const res = await postRequest(
          `${getDomainOrigin()}${travelV2fUrls.v2fCountryResults()}`,
          {
            ...p,
            ...visa2FlyFilters[selectedFilter],
          }
        );
        if (res?.data) {
          setAllResults([...allResults, ...res?.data?.data?.countries]);
        }
      } catch (error: any) {
        const toastMsg =
          error?.response?.status === 500
            ? ErrorMessages.GENERIC
            : error?.response?.data?.message;

        setShowToast(toastMsg);
        setTimeout(() => {
          setShowToast(null);
        }, 2000);
        console.log({ error });
      }
    }
  };

  const handleBack = () => {
    setIsLoading(true);
    sendVasEvents(TravelInsuranceTrackEvents.TAP_BTN_INT_BACK, EventType.TAP);
    setTimeout(() => {
      if (
        router?.query?.utm_medium === UTM_MEDIUM_HOMEPAGE ||
        router?.query?.utm_medium === UTM_MEDIUM_EXPLORE_TAB
      )
        router?.push('/myaccount');
      else router?.back();
    }, 200);
  };

  return (
    <FlexContainer styles={{ alignItems: 'flex-start', maxWidth: '480px' }}>
      {isLoading && <BasicLoader />}
      <HeaderBanner>
        <video
          width="100%"
          height="100%"
          controls={false}
          muted
          autoPlay
          playsInline
          loop
          poster={ASSETS.V2F_HEAD_IMAGE}
        >
          <source src={ASSETS.SKY_VIDEO} />
          Your browser does not support the video tag.
        </video>
        <BackIcon onClick={() => handleBack()}>
          <Image
            src={ASSETS.BACK_ARROW}
            width={'24px'}
            height={'24px'}
            style={{ cursor: 'pointer' }}
          />
        </BackIcon>
        <Gradient />
      </HeaderBanner>
      <Section>
        <PageContainer>
          <SectionSubtitle>{'Visa made simple'}</SectionSubtitle>
          <TitleGroup>
            {v2fTaglines?.map((tag) => (
              <SectionTitle key={tag}>{tag}</SectionTitle>
            ))}
          </TitleGroup>
          <Searchbar
            searchResult={{ val: searchResult, set: setSearchResult }}
            handleClick={({ country = '' }) => {
              sendVasEvents(
                VasTrackEvents.TAP_BTN_COUNTRY_SEARCH,
                EventType.TAP,
                { destination_countries: !!country ? country : undefined }
              );
              router?.push(
                `${VasFeBaseUrl}/${country}${getQueryParams(query)}`
              );
            }}
          />
        </PageContainer>
        {!!searchResult ? (
          <>
            <PageContainer>
              <PopularTitle>{'Search result'}</PopularTitle>
              <Card
                key={searchResult?.code}
                {...searchResult}
                handleClick={handleCardClick}
              />
            </PageContainer>
          </>
        ) : (
          !isLoading && (
            <>
              {allResults?.length === 0 ? (
                <NoResult>
                  <Image
                    src={ASSETS.GRAY_FALLEN_GLASS}
                    width={'80px'}
                    height={'80px'}
                    alt="back"
                  />
                  <NoResultsText>{'Couldn’t find any result'}</NoResultsText>
                </NoResult>
              ) : (
                <>
                  <PageContainer>
                    <PopularTitle>{'Pick where you’d like to go'}</PopularTitle>
                    <Filters>
                      {Object.keys(visa2FlyFilters)?.map((filter) => (
                        <FilterPill
                          key={filter}
                          active={selectedFilter === filter}
                          onClick={() => {
                            if (selectedFilter !== filter) {
                              setSelectedFilter(filter);
                              sendVasEvents(
                                VasTrackEvents.TAP_BTN_COUNTRY_SEARCH_FILTER,
                                EventType.TAP,
                                {
                                  category: filter,
                                }
                              );
                            }
                          }}
                        >
                          {filter}
                        </FilterPill>
                      ))}
                    </Filters>
                    <InfiniteScroll
                      dataLength={allResults?.length}
                      next={() => handleScroll()}
                      hasMore={true}
                      loader={false}
                    >
                      {allResults?.length > 0 && (
                        <CardsContainer>
                          {allResults?.map((card: ICard) => (
                            <Card
                              key={card?.code}
                              {...card}
                              handleClick={handleCardClick}
                            />
                          ))}
                        </CardsContainer>
                      )}
                    </InfiniteScroll>
                  </PageContainer>
                </>
              )}
            </>
          )
        )}
      </Section>
      {!!showToast && <Toast toastMessage={showToast} toastType="error" />}
    </FlexContainer>
  );
};

export default Index;
